// JWT / session helpers for idle timeout + Remember me
import { safeGet, safeSet, safeRemove } from './safeStorage';
import { STORAGE_KEYS } from '../constants/storageKeys';

/** Default idle window without Remember me (ms). */
export const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000;
/** Idle window with Remember me (ms). */
export const REMEMBER_ME_IDLE_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000;
/** Near-expiry refresh window for default tokens. */
export const REFRESH_THRESHOLD_DEFAULT_MS = 30 * 60 * 1000;
/** Near-expiry refresh window for Remember-me tokens. */
export const REFRESH_THRESHOLD_REMEMBER_MS = 24 * 60 * 60 * 1000;
/** Min interval between /auth/refresh calls. */
export const REFRESH_THROTTLE_MS = 5 * 60 * 1000;
/** Throttle for writing lastActivity to storage. */
export const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;

export const AUTH_EXPIRED_EVENT = 'auth:expired';

/**
 * Decode JWT payload (no signature verify — client convenience only).
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function tokenHasRememberMe(token) {
  const payload = decodeJwtPayload(token);
  return Boolean(payload?.remember_me);
}

export function getIdleTimeoutMs(token) {
  return tokenHasRememberMe(token) ? REMEMBER_ME_IDLE_TIMEOUT_MS : IDLE_TIMEOUT_MS;
}

export function getRefreshThresholdMs(token) {
  return tokenHasRememberMe(token) ? REFRESH_THRESHOLD_REMEMBER_MS : REFRESH_THRESHOLD_DEFAULT_MS;
}

export function getTokenExpirationMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

/**
 * Set Discourse SSO cookie to match JWT idle window.
 * @param {string} token
 */
export function setAuthTokenCookie(token) {
  if (!token || typeof document === 'undefined') return;
  const expires = new Date(Date.now() + getIdleTimeoutMs(token));
  document.cookie = `auth_token=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

export function touchLastActivity(force = false) {
  const now = Date.now();
  if (!force) {
    const prev = Number(safeGet(STORAGE_KEYS.lastActivity) || 0);
    if (prev && now - prev < ACTIVITY_WRITE_THROTTLE_MS) return;
  }
  safeSet(STORAGE_KEYS.lastActivity, String(now));
}

export function getLastActivityMs() {
  const raw = safeGet(STORAGE_KEYS.lastActivity);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function clearSessionActivity() {
  safeRemove(STORAGE_KEYS.lastActivity);
  safeRemove(STORAGE_KEYS.lastTokenRefresh);
}

export function emitAuthExpired(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail }));
  } catch (e) {
    console.warn('Could not dispatch auth:expired event', e);
  }
}
